import { cn } from '@repo/ui/lib/utils'
import { useRouterState } from '@tanstack/react-router'
import { useEffect } from 'react'
import { ErrorBoundary } from '@/components/error-boundary'
import { FooterFull } from '@/components/footer'
import { NavbarResizable } from '@/components/nav'
import { footer } from '@/config/footer'
import { useMenuContent } from '@/config/navigation'
import { scrollToHash, useScrollRestoration } from '@/lib/utils/scroll'

interface WebLayoutProps {
  children: React.ReactNode
  centered?: boolean
}

export function WebLayout({ children, centered = false }: WebLayoutProps) {
  const menu = useMenuContent()
  const location = useRouterState({
    select: (state) => state.location ?? null,
  })

  const { previousPathRef } = useScrollRestoration()

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }

    const pathname = location?.pathname ?? null
    const hash = location?.hash ?? null
    const previousPath = previousPathRef.current

    if (!hash) {
      if (pathname && previousPath !== pathname) {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
      }
      previousPathRef.current = pathname
      return
    }

    if (pathname && previousPath !== pathname) {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
    }

    previousPathRef.current = pathname
    scrollToHash(hash, { updateHistory: false })
  }, [location?.hash, location?.pathname, previousPathRef])

  return (
    <div className="relative grid min-h-svh grid-rows-[auto_1fr_auto] overflow-x-clip bg-background">
      <NavbarResizable content={menu} className="px-3 md:px-6" />
      <main className={cn('relative z-10 flex-1', centered && 'flex items-center justify-center')}>
        <ErrorBoundary>{children}</ErrorBoundary>
      </main>
      <FooterFull content={footer} />
    </div>
  )
}
